import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { PASSWORD_RECOVERY_REDIRECT } from '../../config/authUrls';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, user, userProfile, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: 'admin@hotelpartner.com',
    password: 'Admin123!'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  // Handle automatic redirect when user is already logged in
  useEffect(() => {
    // Only redirect if we have both user and profile loaded
    if (!loading && user && userProfile) {
      navigate('/executive-overview', { replace: true });
    }
  }, [user, userProfile, loading, navigate]);

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsResetting(true);
    setResetError(null);
    setResetSuccess(null);

    try {
      const normalizedEmail = resetEmail.toLowerCase().trim();
      
      // 🎯 CRITICAL: Use shared constant for consistent password recovery redirect
      // All password recovery emails MUST redirect to /partner-password-reset
      console.log('🔐 PASSWORD RESET: Sending recovery email to:', normalizedEmail);
      console.log('🔗 REDIRECT TARGET:', PASSWORD_RECOVERY_REDIRECT);
      console.log('⚠️  IMPORTANT: Check DevTools Network tab - POST /auth/v1/recover body must include redirect_to=' + PASSWORD_RECOVERY_REDIRECT);
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
        redirectTo: PASSWORD_RECOVERY_REDIRECT,
      });

      if (resetError) {
        console.error('❌ PASSWORD RESET ERROR:', resetError);
        setResetError(`Password reset failed: ${resetError.message}`);
        setIsResetting(false);
        return;
      }

      console.log('✅ PASSWORD RESET: Email sent successfully');
      console.log('📧 Recovery link will redirect to:', PASSWORD_RECOVERY_REDIRECT);
      
      setResetSuccess('Reset password email sent! Please check your inbox (and spam folder) for the reset link.');
      setResetEmail('');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess(null);
      }, 5000);
      
    } catch (err) {
      console.error('Password reset error:', err);
      setResetError('An unexpected error occurred. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const { error: signInError } = await signIn(formData.email, formData.password);
      
      if (signInError) {
        setError(signInError.message);
        setIsLoading(false);
        return;
      }

      // Don't navigate here - let useEffect handle it after profile loads
      // This prevents premature navigation before userProfile is ready
    } catch (err) {
      setError('An unexpected error occurred');
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // Show loading spinner during auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show forgot password form
  if (showForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Icon name="KeyRound" size={32} className="text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Reset Password</h1>
            <p className="text-muted-foreground mt-2">
              Enter your email address and we'll send you a link to reset your password
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handlePasswordResetRequest}>
            {resetSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Email Sent!</p>
                  <p className="text-xs mt-1">{resetSuccess}</p>
                </div>
              </div>
            )}

            {resetError && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium">Password Reset Failed</p>
                  <p className="text-xs mt-1">{resetError}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <Input
                id="reset-email"
                name="reset-email"
                type="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="admin@example.com"
                disabled={isResetting}
              />
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetEmail('');
                  setResetError(null);
                  setResetSuccess(null);
                }}
                disabled={isResetting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isResetting}
                iconName={isResetting ? "Loader2" : "Mail"}
                iconPosition="left"
              >
                {isResetting ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Icon name="Users" size={32} className="text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Welcome Back</h1>
          <p className="text-muted-foreground mt-2">
            Sign in to access your admin dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Login Failed</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
              Email Address
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              disabled={isLoading}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setResetEmail(formData.email);
                }}
                className="text-xs text-primary hover:text-primary/80 font-medium"
                disabled={isLoading}
              >
                Forgot Password?
              </button>
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              disabled={isLoading}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isLoading}
            iconName={isLoading ? "Loader2" : "LogIn"}
            iconPosition="left"
          >
            {isLoading ? 'Signing In...' : 'Sign In'}
          </Button>
        </form>

        {/* Demo Credentials Section */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            🔑 Demo Credentials
          </h3>
          <div className="space-y-2 text-xs text-blue-800">
            <div className="flex items-center justify-between p-2 bg-white rounded">
              <span className="font-medium">Admin:</span>
              <code className="text-blue-600">admin@hotelpartner.com / Admin123!</code>
            </div>
            <p className="text-xs text-blue-600 mt-2 italic">
              ℹ️ Use these credentials to log in and access all features
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link to="/auth/signup" className="text-primary hover:text-primary/80 font-medium">
              Sign up
            </Link>
          </p>
        </div>

        {/* Partner Login Navigation */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Are you a business partner?
            </p>
            <Button
              onClick={() => navigate('/partner-login')}
              variant="outline"
              className="w-full"
              iconName="Building2"
              iconPosition="left"
            >
              Go to Partner Login
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;